import { Injectable } from '@angular/core';
import { TrainingLevel } from '@cf-shared/data-model/training-level';
import { PouchDoc, isPouchNotFound } from '@cf-shared/data-model/pouch';
import { LoggingService } from './logging.service';
import { untranslatedString } from 'models/translated-string';
import { CommonDbService } from './common-db.service';
import { CloudFunctionsService, isNetworkError } from './cloud-functions.service';
import { generateAsyncSingletonFunction } from '@cf-shared/shared-util';
import { fixupSlotId, isObsoleteSlotId } from './slot-id-fixup';
import { DocWatcher, TransactionId } from 'services/database-wrapper';
import { isDatabaseUnavailable } from 'services/database-unavailable-error';
import { describeError } from '@cf-shared/error-describe';

export { Exercise, CustomExercise, AnyExercise, isCustomExercise, isCustomExerciseById, isCuratedExerciseById, isForkUde, isOwnedCustomExercise } from '@cf-shared/data-model/exercise';
import { Exercise, CustomExercise, isCustomExerciseById, isCuratedExerciseById, isForkUde } from '@cf-shared/data-model/exercise';
import { pickSlotForExercise } from 'util/exercise-slot-lookup';
import { getStagingExerciseAsync } from './global-exercises-staging-handle';
import { computeHasUpdate, isCoachCopy } from './exercise-fork-update';
import { exerciseFromGetExerciseResult } from './exercise-resolve';
import {
  ENGLISH_TEXT_SEGMENT,
  EXERCISE_TEXT_FIELDS,
  ExerciseTextField,
  ExerciseTextLanguage,
  ExerciseTextWriteAction,
  curatedExerciseIdsWithoutName,
  decideExerciseTextWrite,
  exerciseTextDocIdForSegment,
  hydrateExerciseText,
  pickExerciseTextMaps,
} from './text-locale';
import { ExerciseTextCache } from './exercise-text-cache';
import { ExerciseTextSnapshot, saveExerciseDocumentWithoutTextAsync } from './curated-exercise-save';

// Pure myo-rep helpers live in shared for unit-testability; re-export so
// existing `services/exercise-db.service` imports keep working.
export { exerciseUsesMyoReps, getMyoAwareNumSets } from '@cf-shared/data-model/exercise-myoreps';

// The strength-test question is pure and lives in shared for the same reason; re-exported so
// existing `services/exercise-db.service` imports keep working.
export { exerciseUsesRMTesting } from '@cf-shared/data-model/exercise-rm-testing';

// Re-export so callers can pick it up alongside the other exercise-db
// helpers. Actual implementation lives in shared for unit-testability
// (this module pulls in Angular services transitively).
export { getRestSecondsForExercise } from '@cf-shared/data-model/rest-timer';

/* Get an appropriate display name for an exercise. Tolerates undefined input. */
export function getExerciseName(exercise: Exercise | undefined) {
  return (exercise && (exercise.shortname || exercise.name)) || '?';
}

export class ExerciseSlotEntry {
  exerciseId: string; // matches Exercise._id from global_common
}

export class ExerciseSlot extends PouchDoc {
  slotName: string;
  exercises: ExerciseSlotEntry[] = [];
  /** If this is non-empty, this slot will be added to testing template in intake form.
   * All exercises in this list must have the strength level data set!
   */
  testExercises?: ExerciseSlotEntry[];
  allowNoAddedWeight?: boolean;
}

export function getReadableSlotNameFromId(slot: ExerciseSlot, slotId: string) {
  let slotName = slot.slotName;
  let [, slotSubIndexStr, , subSlotHashStr] = slotId.match(/\$([0-9]+)(#([0-9]+))?/) || ['', '0', '', '0'];
  let slotSubIndex = parseInt(slotSubIndexStr) || 0;
  let subSlotHash = parseInt(subSlotHashStr) || 0;
  if (slotSubIndex > 0) {
    let suffix = {
      0: 'secondary',
      1: 'tertiary',
      2: 'quaternary',
    }[subSlotHash] || 'extra';
    if (suffix) {
      slotName += ` (${suffix})`;
    }
  }
  return slotName;
}

// remove the "slot/" prefix
export function getExerciseSlotShortId(slot: ExerciseSlot): string {
  console.assert(slot._id.startsWith('slot/'));
  return slot._id.substring(5);
}

export type ExerciseDbDoc = Exercise | ExerciseSlot;

// data for one level in one BW
// storing levels in each entry seems wasteful, but will scale better when we need to add more classes
export class StrengthClass {
  min1rm_kg: number; // minimum 1rm on this exercise to be in this level
  level: TrainingLevel;
}

// strength class brackets for one BW class
export class StrengthPerBW {
  bw_kg: number;
  classes: StrengthClass[];

  // always make sure the only possible categories are available
  constructor() {
    this.classes = [];
    this.classes.push({ level: TrainingLevel.Intermediate, min1rm_kg: null });
    this.classes.push({ level: TrainingLevel.Advanced, min1rm_kg: null });
  }
}

export class StrengthStandard_Relative {
  idReferenceExercise: string;
  factor1RM_pc: number; // this_exercise_1RM = reference_exercise_1RM * factor1RM_pc/100
}

export class StrengthStandard_Absolute {
  men: StrengthPerBW[] = [];
  women: StrengthPerBW[] = [];
}

export function isStrengthStandardRelative(s: StrengthStandard): s is StrengthStandard_Relative {
  return !('men' in s);
}

export type StrengthStandard = StrengthStandard_Absolute | StrengthStandard_Relative;

// `ExerciseTextLanguage` and the rule that turns it into a pair of maps live in
// `text-locale`, beside every other rule of the `$text/` store. Re-exported here
// because every caller in the app imports it from this service.
export { ExerciseTextLanguage };

/**
 * The exercise document was saved, but its `$text/en-us/@exercise:` siblings
 * were not. It exists so a caller can tell that half-succeeded case from a save
 * that stored nothing: after this error the stored inline text matches what the
 * user typed, so rolling the in-memory copy back to the pre-edit text would make
 * the app disagree with the database.
 */
export class ExerciseTextSaveError extends Error {
  constructor(readonly reason: unknown) {
    // pass the original as `cause` as well, so anything that walks the chain
    // still reaches it. Callers that REPORT the failure should report `reason`
    // itself — the database-recovery path keys off the error object's own shape.
    super('The exercise was saved, but its English $text/ documents were not', { cause: reason });
  }
}

@Injectable()
export class ExerciseDbService {
  get database() { return this.commonDb.database; }

  // Curated exercise text lives in the `$text/` keyed-text store (#4210). It is
  // read ONCE per session and kept as a map, because hydration has to be cheap:
  // `getGlobalExerciseByIdAsync` is called per exercise in loops (the workout
  // loader, the pickers), and three extra document reads per call would be far
  // more expensive than one prefix query for the whole catalog.
  //
  // The cache is a separate object, not fields on this service, because its
  // state belongs to the SIGNED-IN SESSION while this service lives as long as
  // the app does. `shutdownAsync` throws it away and puts a fresh one here, so a
  // read the previous user started completes into an object nobody holds and
  // cannot write over the next user's state. See `exercise-text-cache.ts`.
  private exerciseText: ExerciseTextCache;

  constructor(
    private logging: LoggingService,
    private commonDb: CommonDbService,
    private cloudFunctionsService: CloudFunctionsService,
  ) {
    // assigned HERE and not as a field initializer: a field initializer runs
    // before the constructor parameters above are assigned, so `this.commonDb`
    // would still be undefined
    this.exerciseText = this.createExerciseTextCache();
  }

  private createExerciseTextCache(): ExerciseTextCache {
    // the database is read lazily on each use — it is not open until
    // `initializeAsync` has run, and the cache can outlive a teardown
    return new ExerciseTextCache(() => this.commonDb.database, this.logging);
  }

  initializeAsync = generateAsyncSingletonFunction(async () => {
    // if commonDb is already open locally (warm resume Phase A), skip waiting
    // for remote connection — local PouchDB has all the exercise data
    if (!this.commonDb.database) {
      await this.commonDb.initializeAsync();
    }
  });

  async shutdownAsync() {
    // throw the whole cache away, so a different user (or a different UI
    // language after a reload) starts from a clean read. Discarding the object
    // rather than resetting fields is what makes it impossible for a read still
    // in flight to write into the state the next session owns.
    this.exerciseText.discard();
    this.exerciseText = this.createExerciseTextCache();
  }

  /**
   * Fill one exercise's `name` / `shortname` / `description` from `$text/`.
   *
   * `Authoring` resolves ENGLISH text rather than the language being shown — see
   * `ExerciseTextLanguage`. It used to skip the store altogether and hand back
   * the document as stored, which #4285 made unworkable by deleting the inline
   * fields it relied on. A curated exercise therefore comes back as a COPY,
   * English included, because the stored document holds no text of its own.
   */
  private async hydrateExerciseTextAsync<T extends Exercise>(
    exercise: T,
    language: ExerciseTextLanguage,
  ): Promise<T> {
    const maps = await this.exerciseText.getMapsAsync();
    const { localized, english } = pickExerciseTextMaps(language, maps);
    const hydrated = hydrateExerciseText(exercise, localized, english);
    this.exerciseText.reportCuratedExercisesWithoutName(curatedExerciseIdsWithoutName([hydrated], maps.english));
    return hydrated;
  }

  isValidExerciseId(id: string): boolean {
    return id.startsWith('exercise/');
  }
  async getGlobalExerciseByIdAsync(
    id: string,
    watcher: DocWatcher,
    language: ExerciseTextLanguage = ExerciseTextLanguage.Active,
  ): Promise<Exercise> {
    let exercise: Exercise = undefined;
    try {
      exercise = await this.readCuratedExerciseAsync(id, watcher);
    } catch (e) {
      // a database that could not be read at all travels to the caller; see readCuratedExerciseAsync
      if (isDatabaseUnavailable(e)) {
        throw e;
      }
      // any other failed read is reported and then answered as "not in the catalog". The
      // callers of this method cannot tell that apart from a real gap in the catalog; a
      // caller that must not confuse the two uses getGlobalExerciseByIdOrThrowAsync (#6540).
      this.logging.trackAbnormalException(e, untranslatedString(`Cannot get exercise ${id}`));
    }
    // if the document was not found there is nothing to hydrate
    if (!exercise) {
      return exercise;
    }
    return this.hydrateExerciseTextAsync(exercise, language);
  }

  /**
   * Like getGlobalExerciseByIdAsync, but a read that FAILED is thrown instead of answered
   * as `undefined`. `undefined` here means only "this exercise is not in the catalog" (or
   * a custom exercise, which never is). The intake form's Strength step uses it, because it
   * cleans the user's saved strength answers up to match the exercises it found, and a
   * failed read taken for a missing exercise erased those answers (#6540).
   */
  async getGlobalExerciseByIdOrThrowAsync(
    id: string,
    watcher: DocWatcher,
    language: ExerciseTextLanguage = ExerciseTextLanguage.Active,
  ): Promise<Exercise | undefined> {
    const exercise = await this.readCuratedExerciseAsync(id, watcher);
    // if the document was not found there is nothing to hydrate
    if (!exercise) {
      return exercise;
    }
    return this.hydrateExerciseTextAsync(exercise, language);
  }

  /** read one curated exercise document, unhydrated. `undefined` means not found; every other failure is thrown */
  private async readCuratedExerciseAsync(id: string, watcher: DocWatcher): Promise<Exercise | undefined> {
    // if id is missing, track silently with a stack trace so we can identify the caller
    if (!id) {
      this.logging.trackSilentProblem('getGlobalExerciseByIdAsync called with falsy id', {
        id,
        stack: new Error().stack,
      });
      return undefined;
    }
    // A custom exercise is never in the curated catalog, and that is knowable from the id
    // alone — so answer WITHOUT reading. This is what stops an unreadable curated database
    // from hiding a custom exercise that is perfectly readable: `resolveAnyExerciseByIdAsync`
    // reads this method first and falls back to the user database, and the two databases are
    // separate wrappers that fail independently, so a throw here would skip a fallback that
    // would have succeeded. The same prefix dispatch is still written out by hand in
    // `WorkoutLoaderService.setExerciseById`; the two pages that also ask the cloud use
    // `locateExerciseByIdAsync`.
    // The answer is unchanged either way — the read was always a not-found — so this only
    // removes a pointless read, which these loops make per exercise.
    if (isCustomExerciseById(id)) {
      return undefined;
    }
    let exercise: Exercise = undefined;
    try {
      exercise = await this.commonDb.database.getTypedRecordAsync<Exercise>(id, watcher);
    } catch (e) {
      // if the database could not be read at all, say so rather than answering `undefined`.
      // An empty answer here means "this exercise is not in the catalog", and every caller
      // reads it that way — so collapsing "we could not look" into it tells the user their
      // exercise is gone when it is merely unreachable for a moment, which is the confusion
      // #4737 fixed for the pages and this method then re-introduced beneath them. Letting it
      // travel is what `design-docs/pass-through-db.md` asks of a caller by default, and it is
      // what getAllExercisesAsync and getAllSlotsAsync in this same service already do.
      // Reporting it is wrong twice over: a shutting-down or recovering database is an
      // EXPECTED transient state, and trackAbnormalException would show the user an
      // untranslated toast built from a document id.
      // Every other failure travels too; getGlobalExerciseByIdAsync is the variant that
      // reports it and answers `undefined`.
      if (!isPouchNotFound(e)) {
        throw e;
      }
    }
    return exercise;
  }

  async getAllExercisesAsync(
    watcher?: DocWatcher,
    language: ExerciseTextLanguage = ExerciseTextLanguage.Active,
  ): Promise<Exercise[]> {
    const exercises = await this.commonDb.database.getAllOfType<Exercise>('exercise', { watcher });
    const maps = await this.exerciseText.getMapsAsync();
    const { localized, english } = pickExerciseTextMaps(language, maps);
    const hydrated = exercises.map(exercise => hydrateExerciseText(exercise, localized, english));
    this.exerciseText.reportCuratedExercisesWithoutName(curatedExerciseIdsWithoutName(hydrated, maps.english));
    return hydrated;
  }

  async getExerciseNameByIdAsync(
    id: string,
    watcher: DocWatcher,
    language: ExerciseTextLanguage = ExerciseTextLanguage.Active,
  ): Promise<string> {
    // the reporting read on purpose: this only shows a name, and anything it cannot read
    // already shows as `?<id>` rather than as a different exercise (df-c21e11979ef2)
    let e = await this.getGlobalExerciseByIdAsync(id, watcher, language);
    return e == null ? '?' + id : e.name;
  }

  async slotForIdAsync(idSlot: string, watcher: DocWatcher): Promise<ExerciseSlot> {
    if (!idSlot) return undefined;
    console.assert(idSlot.startsWith('slot/'));

    // remap if old
    idSlot = fixupSlotId(idSlot);

    let slot: ExerciseSlot = undefined;
    try {
      slot = await this.commonDb.database.getTypedRecordAsync<ExerciseSlot>(idSlot, watcher);
    } catch (e) {
      // if the database could not be read at all, say so rather than answering `undefined`,
      // which every caller reads as "no such slot". Same reasoning as getGlobalExerciseByIdAsync
      // above, and it matches getAllSlotsAsync below, which already lets this travel.
      if (isDatabaseUnavailable(e)) {
        throw e;
      }
      if (!isPouchNotFound(e)) {
        this.logging.trackAbnormalException(e, untranslatedString(`Cannot get slot ${idSlot}`));
      }
    }
    return slot;
  }

  /** The (main) slot an exercise belongs to, or `undefined` when no slot lists it.
   *
   *  `undefined` means "no slot lists this exercise" and callers must treat it as that.
   *  This used to answer `allSlots[0]` instead, which is `slot/$abs` — so a user who named
   *  one of the seven unslotted catalog exercises as their "Most Similar Exercise" had their
   *  own exercise counted as abs volume, which is wrong muscles rather than missing ones
   *  (#4978). */
  async slotForGlobalExerciseIdAsync(idExercise: string, watcher: DocWatcher): Promise<ExerciseSlot | undefined> {
    let allSlots = await this.getAllSlotsAsync(watcher);
    let bestSlot = pickSlotForExercise(allSlots, idExercise);
    // custom exercises without a group are expected to have no slot; a curated one having
    // none is a gap in the catalog and worth hearing about
    if (!bestSlot && !isCustomExerciseById(idExercise)) {
      this.logging.trackSilentProblem(`Exercise ${idExercise} is not in any slot`, {});
    }
    return bestSlot;
  }

  async getAllSlotsAsync(watcher?: DocWatcher): Promise<ExerciseSlot[]> {
    let allSlots = await this.commonDb.database.getAllOfType<ExerciseSlot>('slot', { watcher });
    // remove obsolete slots
    return allSlots.filter(s => !isObsoleteSlotId(s._id));
  }

  async getAllTestingSlotsAsync(watcher: DocWatcher): Promise<ExerciseSlot[]> {
    let slots = await this.getAllSlotsAsync(watcher);
    return slots.filter(s => s.testExercises && s.testExercises.length && s.testExercises.length > 0);
  }

  /**
   * Save a CURATED exercise (the admin editor is the only writer): the exercise
   * document WITHOUT its text, and the text as English `$text/` documents.
   *
   * Since #4285 the `$text/` store is the only home of curated exercise text,
   * so the document is written without it; see `saveExerciseDocumentWithoutTextAsync`.
   * The text is written from the snapshot that call hands back, because the
   * object passed in may be the stored one and has then lost its text.
   */
  async saveExerciseAsync(e: Exercise, transaction: TransactionId): Promise<void> {
    const text = await saveExerciseDocumentWithoutTextAsync(e, this.commonDb.database, transaction);
    try {
      await this.saveEnglishExerciseTextAsync(e._id, text, transaction);
    } catch (err) {
      // wrap it, because the two halves fail differently and the caller has to
      // tell them apart: by this point the exercise document IS stored, so the
      // in-memory copy is correct and must NOT be rolled back
      throw new ExerciseTextSaveError(err);
    }
  }

  // Write the three `$text/en-us/@exercise:<id>.<field>` documents for one
  // curated exercise. Custom exercises are never in this store, so they are
  // skipped outright.
  private async saveEnglishExerciseTextAsync(
    exerciseId: string,
    text: ExerciseTextSnapshot,
    transaction: TransactionId,
  ): Promise<void> {
    if (isCustomExerciseById(exerciseId)) {
      return;
    }
    try {
      await this.commonDb.database.withDocWatcherAsync(async watcher => {
        for (const field of EXERCISE_TEXT_FIELDS) {
          await this.saveOneEnglishTextFieldAsync(exerciseId, field, text[field], watcher, transaction);
        }
      });
    } finally {
      // in the FINALLY, not after the loop: a failure part-way still wrote some
      // documents, and the maps this session holds no longer match them
      this.exerciseText.markStale();
    }
  }

  // One field, stored verbatim as the admin typed it.
  private async saveOneEnglishTextFieldAsync(
    exerciseId: string,
    field: ExerciseTextField,
    value: string,
    watcher: DocWatcher,
    transaction: TransactionId,
  ): Promise<void> {
    const docId = exerciseTextDocIdForSegment(exerciseId, field, ENGLISH_TEXT_SEGMENT);
    let existing: PouchDoc & { text?: string } = undefined;
    try {
      existing = await this.commonDb.database.getTypedRecordAsync(docId, watcher);
    } catch (err) {
      // expected: this field has no document yet, which is the normal state for
      // a field that has always been blank (most shortnames)
      if (!isPouchNotFound(err)) throw err;
    }
    switch (decideExerciseTextWrite(existing?.text, value)) {
      case ExerciseTextWriteAction.None:
        return;
      case ExerciseTextWriteAction.Create:
        await this.commonDb.database.saveRecordAsync({ _id: docId, text: value } as PouchDoc, transaction);
        return;
      case ExerciseTextWriteAction.Update:
        existing.text = value;
        await this.commonDb.database.saveRecordAsync(existing, transaction);
        return;
    }
  }

  async saveSlotAsync(s: ExerciseSlot, transaction: TransactionId): Promise<void> {
    // awaited rather than returned: saveRecordAsync now answers with what became of the
    // write (df-068808a2796e), and that answer is for the delete paths, not for here.
    await this.commonDb.database.saveRecordAsync(s, transaction);
  }

  // Resolve the source of an imported UDE: global-id sources resolve from local
  // global_common (no cloud round-trip); UDE-id sources go through:
  //   1. The local `global_exercises` staging DB (Phase 2 of #3198) — populated by the
  //      `byImportedIds` filtered replication. Cheap, offline-friendly, has the latest rev.
  //   2. The `getExercise` cloud function — fallback for the catch-up window (replication
  //      hasn't yet landed the doc) or first-time imports within the same session. Kept
  //      indefinitely; one CF call per imported-UDE page entry only when staging hasn't
  //      caught up — bounded scenario, acceptable cost, removes the race entirely.
  // Returns undefined on not-found. Throws on transport / programmer errors — callers
  // narrow the catch themselves.
  async fetchOriginalExerciseAsync(sourceId: string, watcher: DocWatcher): Promise<CustomExercise | undefined> {
    if (isCuratedExerciseById(sourceId)) {
      // AUTHORING: this document is compared field by field against a stored fork
      // and can be written into it. Against translated text every field would
      // read as changed the moment the user switched language.
      // the throwing read: `undefined` must mean only "the original is gone", because the
      // caller turns it into "Original No Longer Available". A read that failed travels
      // instead (df-d1b2ca933d67).
      return await this.getGlobalExerciseByIdOrThrowAsync(
        sourceId, watcher, ExerciseTextLanguage.Authoring) as CustomExercise | undefined;
    }
    const staged = await getStagingExerciseAsync(sourceId);
    if (staged) return staged;
    // only a not-found answer means the original is gone; a server that could not read it
    // throws, so it is never shown as a deleted original (df-f5a9c683e16d)
    const result = await this.cloudFunctionsService.getExerciseAsync({ exerciseId: sourceId });
    return exerciseFromGetExerciseResult(sourceId, result) as CustomExercise | undefined;
  }

  // Imported-UDE rev-check used by edit-exercise-page (banner + diff dialog) and
  // exercise-stats (banner only). Returns the fetched source doc whenever reachable
  // — callers in the no-user-visible-change branch use it to bump local.sourceRevision.
  // Network failures (offline / DNS / fetch errors, including the wrapped
  // CloudFunctionNetworkError) are swallowed as a non-update so the banner doesn't
  // flicker offline. Server-side errors (HTTP 5xx) and programmer errors
  // (TypeError, unexpected response shape) propagate so they surface in Sentry.
  async checkImportedExerciseForUpdateAsync(
    localExercise: CustomExercise,
    watcher: DocWatcher,
    resolveLocalSourceAsync?: (id: string) => Promise<CustomExercise | undefined>,
  ): Promise<ImportedExerciseUpdateResult> {
    // coach userdb copies are never "checked for update." see `isCoachCopy` and
    // design-docs/coach-mode.md "Post-fork update propagation".
    if (isCoachCopy(localExercise.local)) {
      return { hasUpdate: false, original: null, deleted: false };
    }
    const sourceId = localExercise.local?.sourceExerciseId;
    if (!sourceId) return { hasUpdate: false, original: null, deleted: false };

    // A same-user local fork's source lives only in the user's OWN database — it is never
    // published to the shared `global_exercises` staging DB or the server. The staging/cloud
    // lookup below would therefore miss it and the page would wrongly report the original as a
    // deleted shared exercise (issue #3808). Resolve such a source from the own DB first via the
    // caller-supplied resolver (mirrors `resolveSourceExerciseNameAsync`'s own-DB-first lookup
    // for the "Based on: <name>" line). A genuinely-shared source is authored elsewhere and is
    // never in this user's own DB, so the resolver misses and the staging/cloud path runs
    // exactly as before.
    //
    // `isForkUde` keeps this branch to the case it was written for: a fork whose source is some
    // OTHER document. A share-link import points at ITSELF, so it must fall through to the
    // staging/cloud path below — see `design-docs/public-exercises.md`, "The `isForkUde` test on
    // that branch (`df-3593dfcb9c81`)".
    if (resolveLocalSourceAsync && isCustomExerciseById(sourceId) && isForkUde(localExercise)) {
      const localSource = await resolveLocalSourceAsync(sourceId);
      // A coach's exercise is the exception to "a shared source is never in this user's own
      // DB": the coach sync writes a copy of it there. That copy is not the user's own older
      // exercise, it is the upstream, and it is overwritten in place whenever the coach edits.
      // So a fork of it must fall through to the staging/cloud comparison below, or the
      // client is never told the coach changed the exercise (#6374).
      if (localSource && !isCoachCopy(localSource.local)) {
        // A same-user local source is the user's OWN older exercise, not a shared upstream —
        // there is nothing meaningful to "pull an update" from. So report `deleted: false` (kills
        // the false "no longer available" banner) but force `hasUpdate: false` — otherwise a
        // rev mismatch between the fork's stamped `sourceRevision` and the base's current `_rev`
        // would light a false "Update available" affordance. `original` is still returned for the
        // "Based on: <name>" / manual diff paths.
        return { hasUpdate: false, original: localSource, deleted: false };
      }
    }

    let sourceExercise: CustomExercise | undefined;
    try {
      sourceExercise = await this.fetchOriginalExerciseAsync(sourceId, watcher);
    } catch (err) {
      if (!isNetworkError(err)) throw err;
      // if the source is curated, this was a read of the local catalog database, not the
      // cloud: an aborted read or a TypeError inside PouchDB also passes the network test
      // above. still answer "could not check", but report it so it is not lost
      // (df-d1b2ca933d67)
      if (isCuratedExerciseById(sourceId)) {
        this.logging.trackSilentProblem('checkImportedExerciseForUpdate: curated original read failed', {
          sourceId, err: describeError(err),
        });
      }
      console.log('checkImportedExerciseForUpdate network error', err);
      return { hasUpdate: false, original: null, deleted: false };
    }
    if (!sourceExercise) {
      return { hasUpdate: false, original: null, deleted: true };
    }
    // R3 — banner-fire guard accounts for `lastSeenSourceRevision`. See `computeHasUpdate`.
    // The seam lives in the helper itself so both consumers (edit-exercise-page +
    // exercise-stats) inherit it. Callers that need the *raw* diff for a manual re-pull
    // (exercise-switch `handleCheckForUpdate`, edit-page `openDiffPickerFromCheckForUpdate`)
    // compute the 3-way `buildCoachUpdateDiff(fork, source, base)` directly on `original`
    // even when `hasUpdate` is false (a resolved fork the user re-checks via the menu).
    return {
      hasUpdate: computeHasUpdate(localExercise.local, sourceExercise._rev),
      original: sourceExercise,
      deleted: false,
    };
  }
}

// Result of `checkImportedExerciseForUpdateAsync`: `original` is the fetched source doc
// whenever reachable (truthy on both has-update and up-to-date), null on a swallowed network
// error or a deleted source (`deleted` distinguishes the two).
export interface ImportedExerciseUpdateResult {
  hasUpdate: boolean;
  original: CustomExercise | null;
  deleted: boolean;
}

// Re-export the pure diff helpers so existing import paths
// (`from 'services/exercise-db.service'`) keep working.
export { buildExerciseDiff, buildCoachUpdateDiff } from './exercise-diff';
export type { ExerciseDiffField, ExerciseDiffEntry, ExerciseDiffResolvers } from './exercise-diff';

export { fixupSlotId, isObsoleteSlotId } from './slot-id-fixup';
